import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// =============================================================================
//  Copyright : 2019 semantics GmbH
//  Date      : $Date: 2026-09-10 17:39:18 +0200 (Thu, 10 Sep 2026) $
//  Author    : Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 165452 $
//  Purpose   : editable label with auto input width that combines
//    https://github.com/markbiek/react-editable-label
//    https://github.com/JedWatson/react-input-autosize/blob/master/src/AutosizeInput.js
// =============================================================================
// ---Third-party imports
import React, { forwardRef, useImperativeHandle } from 'react';
import PropTypes from 'prop-types';
import { useResettableState, useState, useEffect, useRef } from 'uix/hooks';
// ---Framework imports
import log from 'uix/logging';
import { isBoolean } from 'ldash';
// ---Local imports
import { KeyboardShortCut } from './keyboardShortcuts';
// ---Globals
const sizerStyle = {
    position: 'absolute',
    top: 0,
    left: 0,
    visibility: 'hidden',
    height: 0,
    overflow: 'scroll',
    whiteSpace: 'pre'
};
const INPUT_PROPS_BLACKLIST = [
    'extraWidth',
    'inputClassName',
    'inputRef',
    'inputStyle',
    'minWidth',
    'onAutosize',
    'placeholderIsMinWidth',
    'onInputMouseEnter'
];
const cleanInputProps = (inputProps)=>{
    INPUT_PROPS_BLACKLIST.forEach((field)=>delete inputProps[field]);
    return inputProps;
};
const copyStyles = (styles, node)=>{
    node.style.fontSize = styles.fontSize;
    node.style.fontFamily = styles.fontFamily;
    node.style.fontWeight = styles.fontWeight;
    node.style.fontStyle = styles.fontStyle;
    node.style.letterSpacing = styles.letterSpacing;
    node.style.textTransform = styles.textTransform;
};
const AutosizeInput = /*#__PURE__*/ forwardRef((props, ref)=>{
    const { className, defaultValue, value, placeholder, placeholderIsMinWidth, extraWidth, id, inputClassName, inputStyle, inputRef, minWidth = 1, onAutosize, style, type, onInputMouseEnter } = props;
    const [inputWidth, setInputWidth] = useState(minWidth);
    const [inputId, setInputId] = useState(id);
    const input = useRef(null);
    const sizer = useRef(null);
    const placeHolderSizer = useRef(null);
    const renderedWidth = useRef(inputWidth);
    useImperativeHandle(ref, ()=>({
            getInput: ()=>input.current,
            focus: (selectText)=>{
                input.current.focus();
                // this will move the cursor to the right in a RTL situation
                input.current.setSelectionRange(0, 0);
                if (selectText) {
                    input.current.select();
                }
            },
            blur: ()=>input.current.blur(),
            select: ()=>input.current.select()
        }));
    // sync the input id when the id prop changes
    // (was UNSAFE_componentWillReceiveProps in the class based form)
    useEffect(()=>{
        setInputId(id);
    }, [
        id
    ]);
    // was componentDidMount
    useEffect(()=>{
        copyInputStyles();
    }, []);
    // was componentDidMount + componentDidUpdate (measure after every render,
    // report width changes via onAutosize)
    useEffect(()=>{
        if (renderedWidth.current !== inputWidth) {
            renderedWidth.current = inputWidth;
            if (typeof onAutosize === 'function') {
                onAutosize(inputWidth);
            }
        }
        updateInputWidth();
    });
    function copyInputStyles() {
        if (!window.getComputedStyle) {
            return;
        }
        const inputStyles = input.current && window.getComputedStyle(input.current);
        if (!inputStyles) {
            return;
        }
        copyStyles(inputStyles, sizer.current);
        if (placeHolderSizer.current) {
            copyStyles(inputStyles, placeHolderSizer.current);
        }
    }
    function updateInputWidth() {
        if (!sizer.current || typeof sizer.current.scrollWidth === 'undefined') {
            return;
        }
        let newInputWidth;
        if (placeholder && (!value || value && placeholderIsMinWidth)) {
            newInputWidth = Math.max(sizer.current.scrollWidth, placeHolderSizer.current.scrollWidth) + 2;
        } else {
            newInputWidth = sizer.current.scrollWidth + 2;
        }
        // add extraWidth to the detected width. for number types, this defaults to 16 to allow for the stepper UI
        const addWidth = type === 'number' && extraWidth === undefined ? 16 : parseInt(extraWidth, 10) || 0;
        newInputWidth += addWidth;
        if (newInputWidth < minWidth) {
            newInputWidth = minWidth;
        }
        if (newInputWidth !== inputWidth) {
            setInputWidth(newInputWidth);
        }
    }
    const sizerValue = [
        defaultValue,
        value,
        ''
    ].reduce((previousValue, currentValue)=>{
        if (previousValue !== null && previousValue !== undefined) {
            return previousValue;
        }
        return currentValue;
    });
    const wrapperStyle = {
        ...style
    };
    if (!wrapperStyle.display) {
        wrapperStyle.display = 'inline-block';
    }
    const resolvedInputStyle = {
        boxSizing: 'content-box',
        width: `${inputWidth}px`,
        ...inputStyle
    };
    const { ...inputProps } = props;
    cleanInputProps(inputProps);
    inputProps.className = inputClassName;
    inputProps.id = inputId;
    inputProps.style = resolvedInputStyle;
    return /*#__PURE__*/ _jsxs("div", {
        className: className,
        style: wrapperStyle,
        children: [
            /*#__PURE__*/ _jsx("input", {
                ...inputProps,
                ref: (el)=>{
                    input.current = el;
                    if (typeof inputRef === 'function') {
                        inputRef(el);
                    }
                },
                onMouseEnter: onInputMouseEnter
            }),
            /*#__PURE__*/ _jsx("div", {
                ref: (el)=>{
                    sizer.current = el;
                },
                style: sizerStyle,
                children: sizerValue
            }),
            placeholder ? /*#__PURE__*/ _jsx("div", {
                ref: (el)=>{
                    placeHolderSizer.current = el;
                },
                style: sizerStyle,
                children: placeholder
            }) : null
        ]
    });
});
AutosizeInput.displayName = 'AutosizeInput';
AutosizeInput.propTypes = {
    className: PropTypes.string,
    defaultValue: PropTypes.any,
    extraWidth: PropTypes.oneOfType([
        // additional width for input element
        PropTypes.number,
        PropTypes.string
    ]),
    id: PropTypes.string,
    inputClassName: PropTypes.string,
    inputRef: PropTypes.func,
    inputStyle: PropTypes.object,
    minWidth: PropTypes.oneOfType([
        // minimum width for input element
        PropTypes.number,
        PropTypes.string
    ]),
    onAutosize: PropTypes.func,
    onChange: PropTypes.func,
    placeholder: PropTypes.string,
    placeholderIsMinWidth: PropTypes.bool,
    style: PropTypes.object,
    value: PropTypes.any
};
const EditableLabel = ({ text, emptyLabel, disableKeys, inputClass, labelClass, id, viewMode, doubleClick = false, keyboardShortcut, selectText = false, onTabKey, onKeyDown, onSave, onCancel, onMouseEnter, onInputMouseEnter, onStartEdit, onEndEdit, style, title, setLastCursorState, readonly = false })=>{
    let [view, setView] = useState(null);
    if (!view) {
        view = viewMode || 'label';
    }
    useEffect(()=>{
        setView(null);
    }, [
        viewMode
    ]);
    let [value, setValue] = useResettableState(text, [
        text
    ]);
    if (readonly) {
        value = text;
        setValue = ()=>null;
    }
    const [previous, setPrevious] = useResettableState(text, [
        text
    ]);
    const textInput = useRef(null);
    useEffect(()=>{
        if (view === 'text') {
            textInput.current.focus(selectText);
        }
    }, [
        view,
        textInput
    ]);
    const closeEditor = ()=>{
        setView('label');
        if (onEndEdit) onEndEdit();
    };
    const keyUp = (e)=>{
        if (disableKeys) {
            return;
        }
        if (e.key === 'Escape') {
            setValue(previous);
            closeEditor();
            if (onCancel) {
                onCancel();
            }
        } else if (e.key === 'Enter') {
            let new_val = e.target.value;
            if (onSave && text != e.target.value) {
                let res = onSave(e.target.value);
                if (isBoolean(res) && !res) {
                    new_val = previous;
                }
            }
            setValue(new_val);
            setPrevious(new_val);
            closeEditor();
        }
    };
    const keyDown = (e)=>{
        if (e.key === 'Tab' && onTabKey) {
            // also send current input text value
            onTabKey(!!e.shiftKey, e.target.value);
            e.preventDefault();
        }
        if (onKeyDown) {
            onKeyDown(e);
        }
    };
    const switchToEditmode = ()=>{
        setView('text');
        if (onStartEdit) onStartEdit();
    };
    const renderLabel = ()=>{
        let handler = doubleClick ? 'onDoubleClick' : 'onClick';
        let props = {
            [handler]: switchToEditmode
        };
        return /*#__PURE__*/ _jsxs("span", {
            className: labelClass,
            id: id,
            onMouseEnter: onMouseEnter,
            style: style,
            title: title,
            ...props,
            children: [
                keyboardShortcut && /*#__PURE__*/ _jsx(KeyboardShortCut, {
                    name: keyboardShortcut,
                    onTrigger: switchToEditmode
                }),
                value || emptyLabel
            ]
        });
    };
    const renderInput = ()=>/*#__PURE__*/ _jsx(AutosizeInput, {
            type: "text",
            ref: textInput,
            value: value,
            inputClassName: inputClass,
            onChange: (e)=>setValue(e.target.value),
            onBlur: (e)=>{
                let lastCursorState = null;
                if (setLastCursorState) {
                    const element = e.target;
                    lastCursorState = {
                        element,
                        setValue,
                        selectionStart: element.selectionStart,
                        selectionEnd: element.selectionEnd
                    };
                }
                const targetValue = e.target.value;
                setTimeout(()=>{
                    // without timeout activeElement is whole body
                    const nextEl = document.activeElement;
                    if (nextEl.classList.contains('specialCharsButton') || !!nextEl.closest('.specialCharPopupContent') || !!nextEl.closest('.specialCharsInlineBar')) {
                        // if clicking sth. within special char popup or inline bar -> keep focus within editableLabel
                        if (lastCursorState) setLastCursorState(lastCursorState);
                        textInput.current.focus();
                    } else {
                        closeEditor();
                        setPrevious(targetValue);
                        if (onSave && text != targetValue) onSave(targetValue);
                    }
                }, 0);
            },
            onKeyUp: keyUp,
            onKeyDown: keyDown,
            onInputMouseEnter: onInputMouseEnter
        });
    return view === 'label' ? renderLabel() : renderInput();
};
EditableLabel.propTypes = {
    text: PropTypes.string.isRequired,
    labelClass: PropTypes.string,
    inputClass: PropTypes.string,
    disableKeys: PropTypes.bool
};
export { EditableLabel, AutosizeInput };
